import {
  AfterViewInit,
  Component,
  computed,
  input, model,
  output,
  signal,
  TemplateRef,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { SynIconComponent, SynInputComponent, SynSpinnerComponent } from '@synergy-design-system/angular';
import { SynInputEvent, SynChangeEvent } from '@synergy-design-system/components';

export interface RowContext<ValueT> {
  $implicit: ValueT;
}

export interface ColumnDescription<ValueT> {
  label: string;
  getField: (value: ValueT) => string;
  style?: string;
}

@Component({
  selector: 'app-table',
  template: `
    @if (search()) {
      <div class="flex-row justify-end">
        <syn-input
          i18n-placeholder="@@search"
          placeholder="Search"
          clearable=""
          autocomplete="off"
          size="small"
          class="justify-end"
          style="width: 20em"
          [(value)]="searchTextField"
          [disabled]="disabled()"
          (synInputEvent)="onSearch($event)"
          (synChangeEvent)="onSearchChange($event)"
          data-e2e="table-search-input"
        >
          <syn-icon name="search_fill" slot="prefix" />
        </syn-input>
      </div>
    }
    <table class="syn-table--alternating">
      <thead>
      <tr>
        @for (column of columns(); track column.label) {
          <th (click)="sortBy(column)" [style]="column.style ?? ''">
            {{ column.label }}
            @if (sortedBy() === column) {
              @if (sortedSign() === +1) {
                ▲
              } @else {
                ▼
              }
            }
          </th>
        }
      </tr>
      </thead>
      <tbody [ngClass]="{'clickable': clickable()}">
        @if (loading()) {
          @for (column of columns(); track column.label) {
            <td>
              <syn-spinner style="font-size: 20px; --track-width: 4px;" />
            </td>
          }
        } @else {
          @for (value of currentPage(); track $index) {
            <ng-container [ngTemplateOutlet]="rowTemplateOrDefault()"
                          [ngTemplateOutletContext]="{ $implicit: value }" />
          }
        }
      </tbody>
    </table>
    <div class="float-right">
      <div class="flex-row gap-1">
        <span>{{ pageStart() }} - {{ pageEnd() }}</span>
        <span i18n="@@of">of</span>
        <span>{{ count() }}</span>
        <button type="button" class="navigation" (click)="gotoFirstPage()">❮❮</button>
        <button type="button" class="navigation" (click)="gotoPrevPage()">❮</button>
        <button type="button" class="navigation" (click)="gotoNextPage()">❯</button>
        <button type="button" class="navigation" (click)="gotoLastPage()">❯❯</button>
      </div>
    </div>

    <ng-template #defaultRow let-value>
      <tr (click)="onRowClick(value)">
        @for (column of columns(); track column.label) {
          <td>
            {{ column.getField(value) }}
          </td>
        }
      </tr>
    </ng-template>
  `,
  styles: [
    `
      table {
        width: 100%;
      }

      .navigation {
        min-width: 1rem;
      }

      .navigation:hover {
        cursor: pointer;
      }

      :host ::ng-deep tbody.clickable tr:hover td {
        background-color: var(--syn-color-neutral-100);
        cursor: pointer;
      }
    `,
  ],
  imports: [
    NgClass,
    NgTemplateOutlet,
    SynInputComponent,
    SynIconComponent,
    SynSpinnerComponent,
  ],
})
export class TableComponent<ValueT> implements AfterViewInit {
  columns = input([] as ColumnDescription<ValueT>[]);
  values = input([] as ValueT[]);
  // How many values should be displayed per page
  pageSize = input(10);
  // Make the rows clickable and highlight them on hover
  clickable = input(false);
  disabled = input(false);

  selectValue = output<ValueT>();

  search = input(true);
  // If you want to implement a custom search instead of a simple text search
  customSearch = input(false);
  searchEvent = output<string>();
  searchTextField = model('');

  // show loading spinner
  loading = input(false);

  @ViewChild('defaultRow')
  private defaultRowChild!: TemplateRef<RowContext<ValueT>>;
  defaultRowTemplate = signal(null as (TemplateRef<RowContext<ValueT>> | null));
  rowTemplate = input(null as (TemplateRef<RowContext<ValueT>> | null));
  rowTemplateOrDefault = computed(() => {
    return this.rowTemplate() ?? this.defaultRowTemplate();
  });

  sortedBy: WritableSignal<null | ColumnDescription<ValueT>> = signal(null);
  sortedSign = signal(+1);

  searchText = signal('');

  filteredValues = computed(() =>
    this.filter(this.values(), this.searchText()),
  );

  sortedValues = computed(() => {
    const column = this.sortedBy() ?? this.columns()[0];
    return this.sort(this.filteredValues(), column, this.sortedSign());
  });

  count = computed(() => this.filteredValues().length);
  offset = signal(0);
  currentPage = computed(() =>
    this.getCurrentPage(this.sortedValues(), this.offset(), this.pageSize()),
  );
  pageStart = computed(() => {
    if (this.count() === 0) return 0;
    return this.offset() + 1;
  });
  pageEnd = computed(() => {
    return Math.min(this.count(), this.offset() + this.pageSize());
  });

  ngAfterViewInit() {
    this.defaultRowTemplate.set(this.defaultRowChild);
  }

  onSearch(event: SynInputEvent) {
    const newValue = (event.target as HTMLInputElement).value;
    if (!this.customSearch()) {
      this.searchText.set(newValue);
      this.offset.set(0);
    }
  }

  onSearchChange(event: SynChangeEvent) {
    // The custom search is only triggered when the user "commits" the changes,
    // e.g. they press enter or leave the input field
    // We do not want to trigger API calls for every key stroke
    const newValue = (event.target as HTMLInputElement).value;
    if (this.customSearch()) {
      this.searchEvent.emit(newValue);
    }
  }

  filter(values: ValueT[], search: string): ValueT[] {
    if (search === '') {
      return values;
    }

    const lowerCaseSearch = search.toLowerCase();
    const filtered: ValueT[] = [];
    for (const value of values) {
      for (const column of this.columns()) {
        const field = column.getField(value);
        if (field.toLowerCase().includes(lowerCaseSearch)) {
          filtered.push(value);
          break;
        }
      }
    }
    return filtered;
  }

  sort(values: ValueT[], by: ColumnDescription<ValueT>, sign: number) {
    return values.toSorted((a, b) => {
      const aKey = by.getField(a);
      const bKey = by.getField(b);
      return sign * aKey.localeCompare(bKey as string);
    });
  }

  sortBy(column: ColumnDescription<ValueT>): void {
    const current = this.sortedBy();
    if (current === column) {
      this.sortedSign.set(-1 * this.sortedSign());
    } else {
      this.sortedBy.set(column);
      this.sortedSign.set(+1);
    }

    this.offset.set(0);
  }

  getCurrentPage(values: ValueT[], offset: number, chunkSize: number): ValueT[] {
    return values.slice(offset, offset + chunkSize);
  }

  gotoPrevPage() {
    let offset = this.offset();
    offset -= this.pageSize();
    if (offset < 0) {
      offset = 0;
    }
    this.offset.set(offset);
  }

  gotoNextPage() {
    const newOffset = this.offset() + this.pageSize();
    if (newOffset < this.count()) {
      this.offset.set(newOffset);
    }
  }

  gotoFirstPage() {
    this.offset.set(0);
  }

  gotoLastPage() {
    const count = this.count();
    if (count <= 0) {
      return;
    }

    const chunkSize = this.pageSize();
    const lastPageIndex = Math.floor((count - 1) / chunkSize);
    const offset = lastPageIndex * chunkSize;
    this.offset.set(offset);
  }

  onRowClick(value: ValueT) {
    this.selectValue.emit(value);
  }
}
